/**
 * Ce que le dock fait quand Hector prend ou écrit le document d'un fil (#1588).
 *
 * Le dock « Demander à Hector » n'offre aucun emplacement au canvas. Quand
 * Hector prend le document du fil qu'il affiche, le dock publie la scène du
 * canvas pour ce fil ; quand Hector y écrit, l'avocat rejoint l'assistant pour
 * le voir. Décisions pures, pour qu'elles se jugent sans React ni pool — plus
 * `lireLaRouteDeLOnglet`, seul lecteur du navigateur, que le dock et le pool
 * partagent.
 */

import { matchPath } from 'react-router-dom';
import type { SignalDAdmissionAgentCanvas, SignalDEcritureAgentCanvas } from '../types/session.types';

/** La route de l'onglet, telle que le navigateur la porte. */
export interface RouteDeLOnglet {
  readonly pathname: string;
  readonly search: string;
}

/**
 * La route que le navigateur porte À L'INSTANT, `null` hors navigateur.
 *
 * Pas la route commitée par React (`useLocation`) : React Router applique une
 * navigation en transition, et la page assistant se charge à la demande. Entre
 * `navigate()` et le commit, `window.location` porte déjà la route choisie par
 * l'avocat alors que le rendu montre encore l'ancienne — la même raison fait
 * lire `window.location` à `AgentV3View` et à `V3ChatInterface`.
 */
export function lireLaRouteDeLOnglet(): RouteDeLOnglet | null {
  if (typeof window === 'undefined') return null;
  return { pathname: window.location.pathname, search: window.location.search };
}

/**
 * L'onglet affiche-t-il l'écran assistant ? Route exacte : `/assistants` ou une
 * page de réglages dont le chemin contient « assistant » n'affichent pas le
 * canvas.
 *
 * Une seule définition pour les décisions qui en dépendent (#1588) : basculer
 * le dock, publier la scène du canvas depuis le dock, et laisser l'écran
 * rejoindre un fil avant une écriture qui juge ce que l'avocat voit.
 */
export function estLEcranAssistant(pathname: string): boolean {
  return matchPath({ path: '/assistant', end: true }, pathname) !== null;
}

/**
 * L'écran assistant de l'onglet affiche-t-il CE fil (`?c=`) ?
 *
 * C'est le fil qui décide, pas la seule route : sur `/assistant?c=X`,
 * l'assistant montre le canvas de X, jamais celui d'un autre fil Y.
 */
export function lEcranAssistantAfficheLeFil(route: RouteDeLOnglet | null, conversationId: string): boolean {
  if (route === null || !estLEcranAssistant(route.pathname)) return false;
  return new URLSearchParams(route.search).get('c') === conversationId;
}

/** Ce que le dock sait de lui-même et de l'onglet au moment d'un signal du pool. */
interface EtatDuDockAuSignal {
  /** Fil affiché par le dock ; `null` tant que le premier envoi n'a pas été confirmé. */
  readonly conversationIdDuDock: string | null;
  /** Connexion du dock ; `null` tant qu'aucune session n'est ouverte. */
  readonly sessionKeyDuDock: string | null;
  /** Route que le navigateur porte AU MOMENT du signal (`lireLaRouteDeLOnglet`). */
  readonly route: RouteDeLOnglet | null;
}

/** Entrée de la bascule : l'écriture annoncée par le pool. */
export interface EntreeDeBasculeDuDock extends EtatDuDockAuSignal {
  readonly signal: SignalDEcritureAgentCanvas;
  /** Ce montage du dock a-t-il déjà basculé ? */
  readonly dejaBascule: boolean;
}

/** Entrée de la publication de scène : la prise annoncée par le pool. */
export interface EntreeDePublicationDeScene extends EtatDuDockAuSignal {
  readonly signal: SignalDAdmissionAgentCanvas;
}

/**
 * **Le fil, sinon la connexion.** Tant que le dock connaît son fil, lui seul
 * fait foi : une clé de session est réattribuée au gré des reconnexions et des
 * évictions. Avant la confirmation du premier envoi, l'agent peut déjà écrire
 * alors que le dock n'a encore aucun identifiant : la connexion est alors la
 * seule identité commune.
 */
function leSignalViseLeFilDuDock(
  signal: SignalDAdmissionAgentCanvas,
  dock: EtatDuDockAuSignal,
): boolean {
  return dock.conversationIdDuDock !== null
    ? signal.conversationId === dock.conversationIdDuDock
    : dock.sessionKeyDuDock !== null && signal.sessionKey === dock.sessionKeyDuDock;
}

/**
 * Vrai ssi le signal vise le fil du dock, que l'assistant ne montre pas déjà ce
 * fil, et que ce dock n'a pas déjà basculé.
 *
 * - **Le fil, sinon la connexion** (`leSignalViseLeFilDuDock`).
 * - **Pas vers le fil que l'assistant montre déjà.** Le dock hors dossier reste
 *   ouvert sur `/assistant` : sur `?c=` du même fil, naviguer ne montrerait rien
 *   de plus. Sur `?c=` d'un AUTRE fil, en revanche, l'assistant montre le canvas
 *   de cet autre fil, jamais celui du dock — sans bascule, Hector écrirait hors
 *   de la vue de l'avocat. La route est celle du NAVIGATEUR : pendant une
 *   transition, la route commitée par React est encore l'ancienne.
 * - **Une fois.** Le pool bloque déjà les trames rejouées ; la mémoire du dock
 *   bloque les écritures successives d'un même tour (ouverture, puis action,
 *   puis génération), qui porteraient chacune une clé neuve.
 */
export function doitBasculerVersLAssistant(entree: EntreeDeBasculeDuDock): boolean {
  if (entree.dejaBascule) return false;
  if (!leSignalViseLeFilDuDock(entree.signal, entree)) return false;
  return !lEcranAssistantAfficheLeFil(entree.route, entree.signal.conversationId);
}

/**
 * Vrai ssi Hector prend le document du fil du dock et que l'onglet n'est pas sur
 * l'assistant.
 *
 * Sur l'assistant, `AgentV3View` publie la scène seul : le dock y publierait son
 * fil par-dessus celui que l'assistant montre, et la résidence retirerait ce
 * document de l'écran de l'avocat. Si Hector écrit ensuite, la bascule change
 * `?c=` et l'assistant republie lui-même.
 */
export function doitPublierLaSceneDuDock(entree: EntreeDePublicationDeScene): boolean {
  if (!leSignalViseLeFilDuDock(entree.signal, entree)) return false;
  return entree.route === null || !estLEcranAssistant(entree.route.pathname);
}
